/**
 * FCM Utility Functions
 *
 * Helper functions for managing FCM tokens and notifications.
 * Supports guest (anonymous) tokens and claim-after-login.
 */

import { getDeviceId } from './firebase';
import { initializeMessaging, getFCMToken } from './firebase';

export const FCM_ANONYMOUS_ID_KEY = 'fcm_anonymous_id';
/** When we use a client-generated anonymous_id (no FCM), backend has no token so push_enabled should be false */
const FCM_ANONYMOUS_SOURCE_KEY = 'fcm_anonymous_id_source';
import { ANONYMOUS_ID_COOKIE_NAME, getAnonymousIdFromCookie } from './cookies';

const ANONYMOUS_ID_COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year

export function setAnonymousIdCookie(anonymousId: string): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${ANONYMOUS_ID_COOKIE_NAME}=${encodeURIComponent(anonymousId)}; path=/; max-age=${ANONYMOUS_ID_COOKIE_MAX_AGE}; SameSite=Lax`;
}

/**
 * Get anonymous_id from localStorage, or restore it from the cookie (e.g. after F5).
 * Use this so the same anonymous_id is used after refresh and subscriptions stay in sync.
 */
export function getStoredAnonymousId(): string | null {
  if (typeof window === 'undefined') return null;
  const id = localStorage.getItem(FCM_ANONYMOUS_ID_KEY);
  if (id) {
    setAnonymousIdCookie(id);
    return id;
  }
  const fromCookie = getAnonymousIdFromCookie(document.cookie);
  if (fromCookie) {
    localStorage.setItem(FCM_ANONYMOUS_ID_KEY, fromCookie);
    return fromCookie;
  }
  return null;
}

/**
 * Ensure we have an anonymous_id for guest subscriptions (push-only for non-logged-in).
 * If not in localStorage, requests notification permission, registers guest token, returns anonymous_id.
 * Returns null if FCM is not configured or permission denied.
 */
export async function ensureGuestAnonymousId(): Promise<string | null> {
  if (typeof window === 'undefined') return null;
  const existing = localStorage.getItem(FCM_ANONYMOUS_ID_KEY);
  if (existing) return existing;

  const granted = await requestNotificationPermission();
  if (!granted) return null;

  let messaging = initializeMessaging();
  if (!messaging && typeof window !== 'undefined') {
    await new Promise((r) => setTimeout(r, 1000));
    messaging = initializeMessaging();
  }
  if (!messaging) return null;

  const token = await getFCMToken(messaging);
  if (!token) return null;

  const result = await registerGuestToken(token, {});
  if (result?.anonymous_id) {
    localStorage.setItem(FCM_ANONYMOUS_ID_KEY, result.anonymous_id);
    localStorage.setItem(FCM_ANONYMOUS_SOURCE_KEY, 'guest');
    setAnonymousIdCookie(result.anonymous_id);
    return result.anonymous_id;
  }
  return null;
}

/**
 * Get or create an anonymous_id for subscriptions when not logged in.
 * Prefers guest FCM (backend-issued id + push). If FCM is not available, creates a client-side UUID
 * so the user can still subscribe (no push until they log in or later register FCM with that id).
 * Same id is used for claim-after-login.
 */
export async function getOrCreateAnonymousId(): Promise<{ id: string; pushEnabled: boolean }> {
  if (typeof window === 'undefined') {
    return { id: '', pushEnabled: false };
  }
  const existing = localStorage.getItem(FCM_ANONYMOUS_ID_KEY);
  const source = localStorage.getItem(FCM_ANONYMOUS_SOURCE_KEY);
  if (existing) {
    setAnonymousIdCookie(existing); // keep cookie in sync for server requests
    return { id: existing, pushEnabled: source === 'guest' };
  }

  const fromGuest = await ensureGuestAnonymousId();
  if (fromGuest) {
    return { id: fromGuest, pushEnabled: true };
  }

  const clientId = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  localStorage.setItem(FCM_ANONYMOUS_ID_KEY, clientId);
  localStorage.setItem(FCM_ANONYMOUS_SOURCE_KEY, 'client');
  setAnonymousIdCookie(clientId);
  return { id: clientId, pushEnabled: false };
}

/**
 * Register guest FCM token (no auth). Returns { id, anonymous_id }; store anonymous_id in localStorage.
 */
export const registerGuestToken = async (
  token: string,
  options?: { device_id?: string; platform?: string; anonymous_id?: string }
): Promise<{ id: string; anonymous_id: string } | null> => {
  try {
    const response = await fetch('/api/fcm/tokens/guest', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token,
        device_id: options?.device_id ?? getDeviceId(),
        platform: options?.platform ?? 'web',
        ...(options?.anonymous_id && { anonymous_id: options.anonymous_id }),
      }),
    });
    const data = (await response.json()) as { id?: string; anonymous_id?: string; detail?: string; error?: string };
    if (!response.ok) return null;
    if (data.anonymous_id) return { id: data.id ?? '', anonymous_id: data.anonymous_id };
    return null;
  } catch {
    return null;
  }
};

/**
 * Claim guest tokens for the current user (call after login). Pass authorizedFetch from auth store.
 */
export const claimGuestTokens = async (
  anonymousId: string,
  authorizedFetch: (url: string, options?: RequestInit) => Promise<Response>
): Promise<boolean> => {
  try {
    const response = await authorizedFetch('/api/fcm/tokens/claim', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ anonymous_id: anonymousId }),
    });
    if (!response.ok) {
      console.error('[FCM] Claim guest tokens failed:', response.status);
      return false;
    }
    return true;
  } catch (error) {
    console.error('[FCM] Claim guest tokens error:', error);
    return false;
  }
};

/**
 * Register FCM token with backend (logged-in user)
 */
export const registerTokenWithBackend = async (
  token: string,
  authorizedFetch: (url: string, options?: RequestInit) => Promise<Response>
): Promise<boolean> => {
  try {
    const response = await authorizedFetch('/api/fcm/tokens', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token: token,
        device_id: getDeviceId(),
        platform: 'web',
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[FCM] Failed to register token:', response.status, errorText);
      return false;
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('[FCM] Token registered successfully with backend');
    }
    return true;
  } catch (error) {
    console.error('[FCM] Error registering token with backend:', error);
    return false;
  }
};

/**
 * Unregister FCM token from backend
 */
export const unregisterTokenFromBackend = async (
  token: string,
  authorizedFetch: (url: string, options?: RequestInit) => Promise<Response>
): Promise<boolean> => {
  try {
    const response = await authorizedFetch(`/api/fcm/tokens?token=${encodeURIComponent(token)}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[FCM] Failed to unregister token:', response.status, errorText);
      return false;
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('[FCM] Token unregistered successfully from backend');
    }
    return true;
  } catch (error) {
    console.error('[FCM] Error unregistering token from backend:', error);
    return false;
  }
};

/**
 * Request notification permission
 */
export const requestNotificationPermission = async (): Promise<boolean> => {
  if (typeof window === 'undefined' || !('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;

  try {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch {
    return false;
  }
};


